import { z } from 'zod';
import { IsoDateTime } from './common.ts';

/** Channel opt-in states (semantics: BB-32). Email and push have a channel state; SMS and WhatsApp only groups. */
export const ChannelState = z.enum(['opted_in', 'subscribed', 'unsubscribed']);
export type ChannelState = z.infer<typeof ChannelState>;
export const ChannelStates = z.strictObject({ email: ChannelState.optional(), push: ChannelState.optional() });
export const GroupStateValue = z.enum(['subscribed', 'unsubscribed']);
export type GroupStateValue = z.infer<typeof GroupStateValue>;
/** `groupId` is the group's `api_id`. */
export const GroupState = z.strictObject({ groupId: z.uuid(), state: GroupStateValue });

export const SubscriptionChannel = z.enum(['email', 'push', 'sms', 'whatsapp']);
export type SubscriptionChannel = z.infer<typeof SubscriptionChannel>;

/** Send settings of a campaign or Canvas: whom the channel state lets through. */
export const AudiencePolicy = z.enum(['subscribed_or_opted_in', 'opted_in_only', 'all_including_unsubscribed']);
export type AudiencePolicy = z.infer<typeof AudiencePolicy>;

export const SubscriptionReason = z.enum([
  'channel_unsubscribed',
  'channel_not_opted_in',
  'group_unsubscribed',
  'group_default_unsubscribed',
  'group_archived',
  'group_channel_mismatch',
]);
export type SubscriptionReason = z.infer<typeof SubscriptionReason>;

/** Whether the message can physically reach the user — separate from consent (SUB-002). */
export const ReachabilityReason = z.enum(['no_push_token', 'os_permission_denied', 'no_email', 'email_invalid', 'no_phone']);
export type ReachabilityReason = z.infer<typeof ReachabilityReason>;

export const Reachability = z.object({ reachable: z.boolean(), reasons: z.array(ReachabilityReason) });
export type Reachability = z.infer<typeof Reachability>;

/** Result of the send-time check that delivery must run right before sending (SUB-004). */
export const SubscriptionDecision = z.object({
  allowed: z.boolean(),
  consent: z.object({
    channelState: ChannelState.nullable(),
    groupState: GroupStateValue.nullable(),
    groupStateSource: z.enum(['explicit', 'default']).nullable(),
  }),
  reachability: Reachability,
  reasons: z.array(SubscriptionReason),
  evaluatedAt: IsoDateTime,
});
export type SubscriptionDecision = z.infer<typeof SubscriptionDecision>;

/** One row of `fixtures/subscriptions/evaluation-cases.json`, shared by the service and SDK-side previews. */
export const SubscriptionEvaluationCase = z.strictObject({
  name: z.string().min(1),
  input: z.strictObject({
    channel: SubscriptionChannel,
    policy: AudiencePolicy,
    /** Stored channel state; null means never set (email and push default to `subscribed`). */
    channelState: ChannelState.nullable(),
    group: z
      .strictObject({
        name: z.string(),
        channel: SubscriptionChannel,
        status: z.enum(['active', 'archived']),
        defaultState: GroupStateValue,
        state: GroupStateValue.nullable(),
      })
      .nullable(),
    reachability: Reachability,
  }),
  expected: z.strictObject({ allowed: z.boolean(), reasons: z.array(SubscriptionReason) }),
});
export const SubscriptionEvaluationCases = z.strictObject({ schemaVersion: z.literal(1), cases: z.array(SubscriptionEvaluationCase).min(1) });
